import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// TODO(theme): add a 'system' mode that follows window.matchMedia('(prefers-color-scheme: dark)') and listens for
// its `change` event. Keep the stored value as 'system' and resolve it to light/dark only when applying `data-theme`.
export type ThemeMode = 'light' | 'dark';

// What the user picked. Device-level, so it is kept across logout (see PRESERVED_LOCAL_STORAGE_KEYS).
export const DEVICE_THEME_PREFERENCE_KEY = 'device-theme-preference';

// The theme applied inside the app: a copy of the preference that only exists while logged in.
// A plain 'light' | 'dark' string so index.html can read it before first paint. deleteStorages() wipes it on logout.
export const APP_THEME_KEY = 'app-theme';

function readAppTheme(): ThemeMode | null {
  try {
    const value = localStorage.getItem(APP_THEME_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function writeAppTheme(mode: ThemeMode | null) {
  try {
    if (mode) localStorage.setItem(APP_THEME_KEY, mode);
    else localStorage.removeItem(APP_THEME_KEY);
  } catch {
    // storage unavailable (private mode / blocked): the in-memory state still drives the UI
  }
}

interface ThemeStore {
  mode: ThemeMode;
  isAppActive: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  setAppActive: (isAppActive: boolean) => void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      mode: 'light',
      // Same check index.html makes before first paint, so React's first render agrees with it.
      isAppActive: readAppTheme() !== null,
      setMode: (mode) => {
        set({ mode });
        if (get().isAppActive) writeAppTheme(mode);
      },
      toggleMode: () => get().setMode(get().mode === 'dark' ? 'light' : 'dark'),
      setAppActive: (isAppActive) => {
        set({ isAppActive });
        writeAppTheme(isAppActive ? get().mode : null);
      },
    }),
    { name: DEVICE_THEME_PREFERENCE_KEY, partialize: (state) => ({ mode: state.mode }) }
  )
);

// The theme to render: the user's preference inside the app, light on public pages.
export const useEffectiveThemeMode = () => useThemeStore((state) => (state.isAppActive ? state.mode : 'light'));
